/**
 * Settings: what is true for you and for the whole workspace, a list of sections on the left and the
 * chosen one beside it. Each agent's own settings live beside the agent; a connection's own details
 * live in Apps.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { AdminPanel } from '../components/admin/AdminPanel'
import { AutomationSettings } from '../components/settings/AutomationSettings'
import { BandSettings } from '../components/settings/BandSettings'
import { CommissionSettings } from '../components/settings/CommissionSettings'
import { ContextSettings } from '../components/settings/ContextSettings'
import { InsuranceSettings } from '../components/settings/InsuranceSettings'
import { MarketDataUsage } from '../components/settings/MarketDataUsage'
import { SkillsSettings, TelegramSettings } from '../components/settings/SettingsPage'
import { TradingRiskSettings } from '../components/settings/TradingRiskSettings'
import { useStore } from '../store'
import { allowanceApi, invitesApi, xaiApi, type Allowance, type Invite, type XaiSubscriptionStatus } from './api'
import { copyApi } from './api'
import { Person, StartCopy, useCopies } from './Copy'
import { TINTS } from './data'
import { Btn, KeyCap, Status, Switch, Tabs, askSure, cx, inputCls, usd } from './kit'
import { Logo } from './logos'
import { SharedMemory } from './Memory'
import { Bar, useTabs } from './Page'
import { useDemo } from './store'

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'workspace', label: 'Workspace' },
  { id: 'account', label: 'Account' },
  { id: 'usage', label: 'Usage' },
  { id: 'memory', label: 'Memory' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'trading', label: 'Trading' },
  { id: 'copy', label: 'Copy trading' },
  { id: 'shortcuts', label: 'Shortcuts' },
  { id: 'about', label: 'About' },
]

/** On a phone the sections are a row of tabs; on a wide screen they are the list beside the page. */
export function SettingsBar() {
  const t = useTabs()
  return (
    <div className="md:hidden">
      <Bar>
        <div className="-mx-2 flex min-w-0 overflow-x-auto px-2 [scrollbar-width:none]">
          <Tabs options={TABS} value={t.settings} onChange={(v) => t.set({ settings: v })} />
        </div>
      </Bar>
    </div>
  )
}

export const Group = ({ title, note, children }: { title: string; note?: string; children: ReactNode }) => (
  <section>
    <div className="mb-[6px] flex h-4 items-center justify-between text-[12px] tracking-normal">
      <h2 className="m-0 text-[12px] font-normal leading-4 text-ws-label">{title}</h2>
      {note && <span className="text-ws-fg-4">{note}</span>}
    </div>
    {children}
  </section>
)
export const Rows = ({ children }: { children: ReactNode }) => <div className="divide-y divide-ws-hair rounded-[8px] border border-ws-line">{children}</div>
export function Row({ left, title, sub, right }: { left?: ReactNode; title: string; sub?: string; right?: ReactNode }) {
  return (
    <div className="flex min-h-10 items-center gap-3 px-3 py-[6px]">
      {left}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] leading-[18px] tracking-normal text-ws-fg">{title}</span>
        {sub && <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">{sub}</span>}
      </span>
      {right}
    </div>
  )
}
const Keys = ({ keys }: { keys: string[] }) => (
  <span className="flex gap-[2px]">
    {keys.map((k) => (
      <KeyCap key={k}>{k}</KeyCap>
    ))}
  </span>
)
export const value = 'shrink-0 text-[13px] tracking-normal text-ws-fg-2'
export const quiet = 'shrink-0 rounded-[6px] px-2 py-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors duration-150 hover:bg-ws-hover hover:text-ws-fg'
/** A real settings section from before agents, shown as it is inside the new page. */
const Embedded = ({ children }: { children: ReactNode }) => <div className="lb-legacy lb-embed space-y-4">{children}</div>

const when = (ms: number) => new Date(ms).toLocaleString('en-US', { weekday: 'long', hour: 'numeric', minute: '2-digit' })

/** What is left of the week's allowance — one figure for all agents together. */
function AllowanceCard() {
  const [a, setA] = useState<Allowance | null>(null)
  useEffect(() => {
    let on = true
    allowanceApi().then((x) => on && setA(x), () => on && setA({ known: false, note: 'The plan did not answer' }))
    return () => {
      on = false
    }
  }, [])
  if (!a) return <div className="h-[104px] rounded-[8px] border border-ws-line" />
  if (!a.known)
    return (
      <div className="rounded-[8px] border border-ws-line px-4 py-[14px]">
        <div className="text-[13px] tracking-normal text-ws-fg-2">{a.note ?? 'The plan does not say how much is left'}</div>
        <p className="m-0 mt-2 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
          The figure comes from your Grok plan itself. It is also shown at{' '}
          <a href="https://grok.com/?_s=usage" target="_blank" rel="noreferrer" className="text-ws-fg-2 underline decoration-ws-rule underline-offset-2 hover:text-ws-fg">
            grok.com
          </a>
          .
        </p>
      </div>
    )
  const left = Math.max(0, Math.min(100, Math.floor(a.percentLeft ?? 0)))
  return (
    <div className="rounded-[8px] border border-ws-line px-4 pb-4 pt-[14px]">
      <div className="flex items-baseline gap-2">
        <span className="text-[28px] font-medium leading-8 tracking-[-0.01em] text-ws-fg">{left}%</span>
        <span className="text-[13px] tracking-normal text-ws-fg-2">left {a.period === 'monthly' ? 'this month' : 'this week'}</span>
        {a.resetsAt && <span className="ml-auto text-[12px] tracking-normal text-ws-fg-3">Resets {when(a.resetsAt)}</span>}
      </div>
      <div className="mt-3 h-[6px] overflow-hidden rounded-[3px] bg-ws-edge" role="img" aria-label={`${left}% of the allowance is left`}>
        <div className={cx('h-full rounded-[3px]', left <= 10 ? 'bg-ws-down' : 'bg-ws-fg')} style={{ width: `${left}%` }} />
      </div>
      <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">One allowance for every agent: your {a.plan ?? 'Grok'} plan. Background jobs and routines draw from it too.</p>
    </div>
  )
}

export function SettingsView() {
  const t = useTabs()
  const tab = t.settings
  const user = useDemo((s) => s.user)
  const admin = user?.role === 'admin'
  const settings = useDemo((s) => s.settings)
  const save = useDemo((s) => s.saveSettings)
  const agents = useDemo((s) => s.agents.length)
  const spaces = useDemo((s) => s.spaces)
  const spaceId = useDemo((s) => s.spaceId)
  const patchSpace = useDemo((s) => s.patchSpace)
  const removeSpace = useDemo((s) => s.removeSpace)
  const logout = useDemo((s) => s.logout)
  const go = useDemo((s) => s.go)
  const space = spaces.find((x) => x.id === spaceId)
  const adminOpen = useStore((s) => s.adminOpen)
  const [xai, setXai] = useState<XaiSubscriptionStatus | null>(null)
  useEffect(() => {
    if (tab === 'usage') void xaiApi.status().then(setXai, () => {})
  }, [tab])
  return (
    <div className="mx-auto flex w-full max-w-[920px] gap-10 px-4 pb-10 pt-5 md:px-10 md:pt-8">
      <nav className="sticky top-8 hidden w-[168px] shrink-0 flex-col gap-px self-start md:flex" aria-label="Settings">
        {TABS.map((x) => (
          <button key={x.id} type="button" onClick={() => t.set({ settings: x.id })} aria-current={tab === x.id} className={cx('flex h-8 items-center rounded-[8px] px-[10px] text-left transition-colors duration-150', tab === x.id ? 'bg-ws-panel text-ws-fg' : 'text-ws-fg-3 hover:bg-ws-hover hover:text-ws-fg')}>
            {x.label}
          </button>
        ))}
      </nav>

      <div className="min-w-0 max-w-[640px] flex-1 space-y-6">
        {tab === 'general' && (
          <>
            <Group title="Answers">
              <Rows>
                <Row title="Search the web while answering" sub="Agents look things up live, on the web and on X" right={<Switch on={settings?.liveSearch !== false} onChange={(v) => void save({ liveSearch: v })} label="Search the web while answering" />} />
              </Rows>
            </Group>
            <Embedded>
              <AutomationSettings />
              <SkillsSettings />
            </Embedded>
          </>
        )}

        {tab === 'workspace' && space && (
          <>
            <Group title="Name">
              <input defaultValue={space.name} key={space.id} onBlur={(e) => e.target.value.trim() && e.target.value.trim() !== space.name && patchSpace(space.id, { name: e.target.value.trim() })} className={inputCls} aria-label="Workspace name" />
            </Group>
            {space.id !== spaces[0]?.id && (
              <Group title="Mark">
                <div className="flex gap-2">
                  {TINTS.map((x) => (
                    <button key={x} type="button" aria-label="Mark colour" aria-pressed={space.tint === x} onClick={() => patchSpace(space.id, { tint: x })} className={cx('flex h-8 w-8 items-center justify-center rounded-[8px] border transition-colors duration-150', space.tint === x ? 'border-ws-focus bg-ws-panel' : 'border-ws-line hover:bg-ws-hover')}>
                      <span className="block h-4 w-4 rounded-[5px]" style={{ background: x }} />
                    </button>
                  ))}
                </div>
              </Group>
            )}
            <Group title="What it has" note="Connected in Apps">
              <Rows>
                <Row left={<Logo name="grok" />} title="AI account" sub="Required: every agent here runs on it" right={<button type="button" className={quiet} onClick={() => go('apps')}>Open Apps</button>} />
                <Row title="Agents" right={<span className={value}>{agents}</span>} />
              </Rows>
            </Group>
            {spaces.length > 1 && space.id !== spaces[0]?.id && (
              <Btn
                variant="danger"
                className="px-3"
                onClick={() =>
                  askSure({
                    title: `Delete ${space.name}?`,
                    body: 'Its agents and group chats leave the screen. Your broker accounts and your other workspaces are not touched.',
                    cta: 'Delete workspace',
                    done: () => removeSpace(space.id),
                  })
                }
              >
                Delete this workspace
              </Btn>
            )}
          </>
        )}

        {tab === 'account' && user && (
          <>
            <Group title="You">
              <Rows>
                <Row left={<span className="block h-6 w-6 shrink-0 rounded-full bg-[linear-gradient(135deg,#ff9bd2,#b28cff)]" />} title={user.name || user.username} sub={`@${user.username}${admin ? ' · admin' : ''}`} right={<Status tone="free">Signed in</Status>} />
              </Rows>
            </Group>
            <Btn variant="outline" className="px-3" onClick={() => void logout()}>
              Sign out
            </Btn>
            {admin && (
              <>
                <Group title="People" note="Only you see this">
                  <Rows>
                    <Row title="Users and what they use" sub="Add someone, set limits, see token use and the API balance" right={<button type="button" className={quiet} onClick={() => useStore.getState().toggleAdmin()}>Open</button>} />
                  </Rows>
                </Group>
                <Invitations />
                <Embedded>
                  <MarketDataUsage />
                </Embedded>
              </>
            )}
            {adminOpen && (
              <div className="lb-legacy">
                <AdminPanel />
              </div>
            )}
          </>
        )}

        {tab === 'usage' && (
          <>
            <Group title="What is left">
              <AllowanceCard />
            </Group>
            <Group title="What pays for the agents">
              <Rows>
                <Row left={<Logo name="grok" />} title={xai?.connected ? 'Your Grok plan' : admin ? 'The server’s API key' : 'No AI account yet'} sub={xai?.connected ? `${agents} agents run on it${xai.email ? ` · ${xai.email}` : ''}` : admin ? 'Paid by use. Connect your own Grok plan in Apps' : 'Connect your Grok account in Apps'} right={<button type="button" className={quiet} onClick={() => go('apps')}>Open Apps</button>} />
                {xai?.connected && <Row title="Last 7 days" sub="Answers and background runs" right={<span className={value}>{xai.week.subscription.runs + xai.week.api.runs} runs</span>} />}
                {xai?.connected && admin && <Row title="When the allowance runs out" sub={xai.fallbackToApi ? 'Agents go on, paid by use on the API key' : 'Agents wait until it resets'} right={<Switch on={xai.fallbackToApi} onChange={(v) => void xaiApi.setFallback(v).then(() => xaiApi.status().then(setXai))} label="Use the API key when the plan runs out" />} />}
              </Rows>
            </Group>
            <Embedded>
              <ContextSettings />
            </Embedded>
          </>
        )}

        {tab === 'memory' && <SharedMemory />}

        {tab === 'notifications' && (
          <Embedded>
            <TelegramSettings />
          </Embedded>
        )}

        {tab === 'trading' && (
          <>
            <Group title="Orders">
              <Rows>
                <Row title="Orders wait for your approval" sub="No agent can change this" right={<Switch on disabled label="Orders wait for approval" />} />
              </Rows>
            </Group>
            <Embedded>
              <TradingRiskSettings />
              <InsuranceSettings />
              <BandSettings />
              {admin && <CommissionSettings />}
            </Embedded>
          </>
        )}

        {tab === 'copy' && <CopySettings />}

        {tab === 'shortcuts' && (
          <Group title="Keyboard and pointer">
            <Rows>
              <Row title="Search and commands" right={<Keys keys={['⌘', 'K']} />} />
              <Row title="Agents" right={<Keys keys={['⌘', 'Y']} />} />
              <Row title="Send" right={<Keys keys={['↵']} />} />
              <Row title="New line" right={<Keys keys={['⇧', '↵']} />} />
              <Row title="Close what is open" right={<Keys keys={['esc']} />} />
              <Row title="More on anything" sub="An agent, a folder, a chat, a card" right={<span className={value}>Right-click, or a long press</span>} />
            </Rows>
          </Group>
        )}

        {tab === 'about' && (
          <Group title="Liberabank">
            <Rows>
              <Row title="The screens from before agents" sub="The same data, the older look — kept as the way back" right={<a href="/classic.html" className={quiet}>Open</a>} />
            </Rows>
          </Group>
        )}
      </div>
    </div>
  )
}

/** Invitations: one link lets one person in. They get a Liberabank of their own. */
function Invitations() {
  const say = useDemo((s) => s.say)
  const [data, setData] = useState<{ invites: Invite[]; people: { id: string; name: string; joinedAt: number }[] } | null>(null)
  const load = () => void invitesApi.list().then(setData, () => {})
  useEffect(load, [])
  const open = (data?.invites ?? []).filter((i) => !i.usedBy && i.expiresAt > Date.now())
  const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return (
    <Group title="Invite someone" note="One link, one person">
      <Rows>
        {open.map((i) => (
          <Row
            key={i.token}
            title={i.url.replace(/^https?:\/\//, '')}
            sub={`Not used yet · good until ${day(i.expiresAt)}`}
            right={
              <span className="flex shrink-0 items-center">
                <button type="button" className={quiet} onClick={() => void navigator.clipboard.writeText(i.url).then(() => say('Link copied'), () => say(i.url))}>
                  Copy link
                </button>
                <button type="button" className={quiet} onClick={() => void invitesApi.revoke(i.token).then(load)}>
                  Take back
                </button>
              </span>
            }
          />
        ))}
        {(data?.people ?? []).map((p) => (
          <Row key={p.id} title={p.name} sub={`Joined ${day(p.joinedAt)}`} right={<Status tone="free">Has an account</Status>} />
        ))}
        <Row
          title="A new invitation"
          sub="They get their own Liberabank, accept the terms themselves, and connect their own accounts"
          right={
            <button type="button" className={quiet} onClick={() => void invitesApi.create().then(load, (e) => say(e instanceof Error ? e.message : 'That did not work'))}>
              Make a link
            </button>
          }
        />
      </Rows>
    </Group>
  )
}

/** Copy trading: who you copy, who may copy you, and — for whoever runs the server — whether orders are really sent. */
function CopySettings() {
  const { data, load } = useCopies()
  const admin = useDemo((s) => s.user?.role === 'admin')
  const settings = useDemo((s) => s.settings)
  const save = useDemo((s) => s.saveSettings)
  const openCopy = useDemo((s) => s.openCopy)
  const [leader, setLeader] = useState<{ id: string; name: string } | null>(null)
  if (!data) return <div className="h-24 rounded-[8px] border border-ws-line" />
  const allow = settings?.copy?.allow === true
  const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return (
    <>
      {!data.live && (
        <p className="m-0 rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
          <span className="text-ws-warn">Rehearsal.</span> Copying is being tried out on this server: every copied trade is worked out and written down, and nothing is sent to a broker.
        </p>
      )}
      <Group title="People you copy" note="Each is its own sub-account in the portfolio">
        <Rows>
          {data.copies.filter((c) => c.status !== 'stopped').map((c) => (
            <Row key={c.id} left={<Person name={c.leader.name} />} title={c.leader.name} sub={`Since ${day(c.since)} · ${usd(c.putIn)} put in · worth ${usd(c.value)}`} right={<button type="button" className={quiet} onClick={() => openCopy(c.id)}>Open</button>} />
          ))}
          {data.leaders.filter((l) => !l.copying).map((l) => (
            <Row key={l.id} left={<Person name={l.name} />} title={l.name} sub="Lets you copy their trades" right={<button type="button" className={quiet} onClick={() => setLeader(l)}>Start copying</button>} />
          ))}
          {!data.copies.some((c) => c.status !== 'stopped') && !data.leaders.length && <Row title="Nobody yet" sub="Someone who invited you and lets you copy them shows up here" />}
        </Rows>
      </Group>
      <Group title="People who copy you">
        <Rows>
          <Row title="People I invited may copy my trades" sub="They switch it on themselves, with their own money and their own limits" right={<Switch on={allow} onChange={(v) => void save({ copy: { allow: v } })} label="People I invited may copy my trades" />} />
          {data.followers.map((f, i) => (
            <Row key={i} left={<Person name={f.name} />} title={f.name} sub={`Since ${day(f.since)}`} right={<Status tone={f.status === 'active' ? 'free' : 'off'}>{f.status === 'active' ? 'Copying' : f.status === 'paused' ? 'Paused' : 'Stopped'}</Status>} />
          ))}
        </Rows>
        <p className="m-0 mt-2 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">You never see what they hold or how much they put in, and you place no order for them: their own account copies yours, by rules they set and can stop at any time.</p>
      </Group>
      {admin && <CopyMode live={data.live} onChanged={load} />}
      <StartCopy leader={leader} onClose={() => setLeader(null)} onStarted={load} />
    </>
  )
}

/** The server's own switch, for whoever runs it: rehearsal (nothing is sent) or live. */
function CopyMode({ live, onChanged }: { live: boolean; onChanged: () => void }) {
  const say = useDemo((s) => s.say)
  const set = (on: boolean) => void copyApi.setMode(on).then(() => (say(on ? 'Copying is live: follower orders are sent' : 'Copying is in rehearsal: nothing is sent'), onChanged()), (e) => say(e instanceof Error ? e.message : 'That did not work'))
  return (
    <Group title="This server" note="Only you see this">
      <Rows>
        <Row
          title="Send copied orders for real"
          sub={live ? 'Live: a follower’s copy sends real orders to their own broker' : 'Rehearsal: every copied trade is worked out and written down, none is sent'}
          right={
            <Switch
              on={live}
              onChange={(v) =>
                v
                  ? askSure({ title: 'Go live with copy trading?', body: 'From now on, when someone copies a trade, a real order goes to their own brokerage account. Watch a rehearsal first: a copy’s page shows what it would have sent.', cta: 'Go live', done: () => set(true) })
                  : set(false)
              }
              label="Send copied orders for real"
            />
          }
        />
      </Rows>
    </Group>
  )
}
